import { useMediaQuery, useToast } from "@chakra-ui/react";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearError, dehydrate, selectAuth, selectError } from "store/slices";

import { useDebounce } from "hooks";
import toLower from "lodash/toLower";

import configs, { isProd } from "config";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import { isSafeRedirect } from "utils/urls";
import { persistUrl } from "utils";
import { useLocation } from "react-router";
import ls from "utils/secureStorage";

// import EventEmitter from "lib/EventEmitter";

interface IErrorContext {
  toast: ReturnType<typeof useToast>;
}

const ErrorContext = createContext<Partial<IErrorContext>>({});

const msgFilters = ["no referrals", "typeError: failed to fetch"];

// let count = 0;
const ErrorContextProvider = (props: any) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const redebounce = useDebounce();
  const [loggedOut, setLoggedOut] = useState(false);

  // const { isOpen, onClose } = useDisclosure();

  // const { isOpen, close } = useInactivityMonitor(0.5);

  // console.log("AuthorizationModal", { isOpen: isOpen("authorization"), count: count++ });

  const { next } = useSelector(selectError);
  const { payload } = useSelector(selectAuth);
  const prevError = useRef<Omit<typeof next, "id" | "action"> | null>(null);
  const hasFilters = useMemo(
    () => !!next?.message && msgFilters.filter((msg) => msg === toLower(next?.message ?? "")).length > 0,
    [next]
  );

  // console.log("Next Error Message", next?.message, hasFilters);

  const autoLogOut = useCallback(() => {
    if (
      (next?.message && toLower(next.message).includes("authorization failed")) ||
      (next?.message && toLower(next.message).includes("token invalid")) ||
      (next?.message && toLower(next.message).includes("unauthorized"))
    ) {
      dispatch(dehydrate());

      const continueTo = window.location.pathname;

      const isSafeRto = isSafeRedirect({ url: continueTo!, skip: !isProd });
      const isExempted = (path: string) => [configs.paths.login, configs.paths.register].includes(path);

      if (continueTo && isSafeRto && !isExempted(continueTo)) {
        window.location.replace(`${configs.paths.login}?rto=${continueTo}`);
      } else if (!isExempted(window.location.pathname)) {
        window.location.replace(configs.paths.login);
      }

      // navigate(`${config.paths.login}?rto=${continueTo}`, { replace: true });
      setLoggedOut(true);
    }
  }, [next, dispatch]);

  const hasErrorValues = useMemo(() => !isEmpty(Object.values(next).filter(Boolean)), [next]);
  // console.log("Next Error", next, hasErrorValues);

  // const handleClose = () => {
  //   // close("authorization")();
  //   onClose();
  //   window.localStorage.setItem("lastActive", new Date().toISOString());
  //   // setLoginPasswordOverlayOpen(false);
  // };

  // const handleOpen = useCallback(() => {
  //   onOpen();
  //   // setLoginPasswordOverlayOpen(true);
  // }, [onOpen]);

  const { pathname } = useLocation();
  const [isMobile] = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    if (hasErrorValues && !!next?.message && pathname !== "/cico") {
      if (next.action?.payload.status === 403) {
        const URL = persistUrl();
        window.location.replace(`/${URL}`);
      }

      if (String(next.message).toLowerCase() === "accountnamerequired") {
        return;
      }

      if (!isEqual(prevError.current, omit(next, ["id", "action"])) && !hasFilters) {
        toast({
          // position: "bottom-right",
          position: isMobile ? "top" : "bottom-right",
          title: "Error",
          description: typeof next.message === "object" ? "Something went wrong" : next.message,
          status: "error",
          duration: 9000,
          isClosable: true,
          onCloseComplete: () => {
            dispatch(clearError("next"));
            prevError.current = null;
            // setTimeout(() => (prevError.current = null), 1000);
          },
        });

        prevError.current = omit(next, ["id", "action"]);
      }
    }

    if (hasErrorValues && pathname === "/cico") {
      // console.log(next, "THE MESSAGE");

      toast({
        position: "bottom-right",
        title: "Error",
        description: next.message,
        status: "error",
        duration: 6000,
        isClosable: true,
        onCloseComplete: () => {
          dispatch(clearError("next"));
          prevError.current = null;
        },
      });

      if (next?.message === "Unauthorized") {
        ls.remove("cico_auth");
        ls.remove("cico_balance");
        toast({
          position: "bottom-right",
          title: "Error",
          description: `${next.message}, please signin`,
          status: "error",
          duration: 9000,
          isClosable: true,
          onCloseComplete: () => {
            dispatch(clearError("next"));
            prevError.current = null;
            // setTimeout(() => (prevError.current = null), 1000);
          },
        });

        window.location.replace("/cico");
      }
    }
  }, [
    next,
    hasFilters,
    dispatch,
    toast,
    autoLogOut,
    redebounce,
    hasErrorValues,
    loggedOut,
    payload?.isSignedIn,
    pathname,
    isMobile,
  ]);

  return <ErrorContext.Provider value={{ toast }}>{props.children}</ErrorContext.Provider>;
};

export default ErrorContextProvider;
